import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SegmentRoutesWindow } from '../Segmentation';
import { RunFilterPanel } from '../RunFilter';
import { useRouteInfo } from '../RouteInfo/useRouteInfo';
import { API_BASE } from '../../config';
import './ControlPanel.css';

const TABS = [
  { id: 'routes',       label: 'Routes',       icon: '' },
  { id: 'segmentation', label: 'Segment',      icon: '' },
  { id: 'playback',     label: 'Playback',     icon: '' },
  { id: 'analysis',     label: 'Analysis',     icon: '' },
];

const LAYER_COLORS = {
  sopRoutes:   '#e603ff',
  vesselRuns:  '#ff8103',
  selectedRun: '#ffa303',
};

const LAYER_LABELS = {
  sopRoutes:   'SOP Routes',
  vesselRuns:  'Vessel Runs',
  selectedRun: 'Selected Route',
};

/* ── Collapsible Section ── */
const Section = ({ title, icon, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cp-section">
      <button className="cp-section-toggle" onClick={() => setOpen(o => !o)}>
        <span className="cp-section-arrow">{open ? '▾' : '▸'}</span>
        {icon && <span className="cp-section-icon">{icon}</span>}
        <span className="cp-section-title">{title}</span>
      </button>
      {open && <div className="cp-section-body">{children}</div>}
    </div>
  );
};

/* ── Compact Run List Item ── */
const RunListItem = ({ run, isSelected, onClick }) => {
  const props = run.properties || {};
  const date = props.start_time
    ? new Date(props.start_time).toLocaleDateString()
    : '';
  const distLabel = props.distance_nm != null
    ? `${Number(props.distance_nm).toFixed(1)} NM`
    : '';
  const speedLabel = props.avg_speed_knots != null
    ? `${Number(props.avg_speed_knots).toFixed(1)} kts`
    : '';

  return (
    <button
      className={`cp-run-item ${isSelected ? 'cp-run-item-active' : ''}`}
      onClick={() => onClick(props.run_id)}
      title={props.run_name}
    >
      <div className="cp-run-item-main">
        <span className="cp-run-item-name">{props.run_name || `Run ${props.run_id}`}</span>
        <span className="cp-run-item-date">{date}</span>
      </div>
      <div className="cp-run-item-meta">
        {distLabel && <span>{distLabel}</span>}
        {speedLabel && <span>{speedLabel}</span>}
      </div>
    </button>
  );
};

/* ── Inline Playback Controls ── */
const InlinePlayback = ({
  route,
  isPlaying,
  onPlayingChange,
  playbackIndex,
  onPlaybackIndexChange,
  onPlaybackActiveChange,
}) => {
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const intervalRef = useRef(null);

  const totalPoints = route?.geometry?.coordinates?.length || 0;
  const progress = totalPoints > 1 ? (playbackIndex / (totalPoints - 1)) * 100 : 0;

  const currentCoord = route?.geometry?.coordinates?.[playbackIndex];
  const currentSog = currentCoord?.[3];
  const currentTimestamp = currentCoord?.[2];

  const formatTime = (ts) => {
    if (!ts) return '--:--:--';
    try { return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return '--:--:--'; }
  };

  // Auto-advance when playing — base interval is 1 000 ms (1 s per point at 1×)
  useEffect(() => {
    if (isPlaying && playbackIndex < totalPoints - 1) {
      intervalRef.current = setInterval(() => {
        onPlaybackIndexChange(prev => {
          const next = typeof prev === 'function' ? prev : prev;
          if (next >= totalPoints - 1) {
            onPlayingChange(false);
            return next;
          }
          return next + 1;
        });
      }, 1000 / playbackSpeed);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, playbackIndex, totalPoints, playbackSpeed, onPlayingChange, onPlaybackIndexChange]);

  const handlePlay = () => {
    // Activate playback mode if not already
    if (onPlaybackActiveChange) onPlaybackActiveChange(true);
    if (playbackIndex >= totalPoints - 1) onPlaybackIndexChange(0);
    onPlayingChange(true);
  };

  const handlePause = () => { onPlayingChange(false); };

  const handleStop = () => {
    onPlayingChange(false);
    onPlaybackIndexChange(0);
    if (onPlaybackActiveChange) onPlaybackActiveChange(false);
  };

  const skip = (dir) => {
    const step = Math.max(1, Math.floor(totalPoints * 0.05));
    onPlaybackIndexChange(prev => Math.max(0, Math.min(totalPoints - 1, prev + dir * step)));
  };

  return (
    <div className="cp-playback">
      <div className="cp-pb-info">
        <div className="cp-pb-info-item">
          <span className="cp-pb-label">Point</span>
          <span className="cp-pb-value">{playbackIndex + 1} / {totalPoints}</span>
        </div>
        {currentTimestamp && (
          <div className="cp-pb-info-item">
            <span className="cp-pb-label">Time</span>
            <span className="cp-pb-value">{formatTime(currentTimestamp)}</span>
          </div>
        )}
        {currentSog != null && (
          <div className="cp-pb-info-item">
            <span className="cp-pb-label">Speed</span>
            <span className="cp-pb-value">{Number(currentSog).toFixed(1)} kts</span>
          </div>
        )}
      </div>

      <div className="cp-pb-slider-wrap">
        <input
          type="range"
          className="cp-pb-slider"
          min="0"
          max={Math.max(0, totalPoints - 1)}
          value={playbackIndex}
          onChange={(e) => {
            if (onPlaybackActiveChange) onPlaybackActiveChange(true);
            onPlaybackIndexChange(parseInt(e.target.value, 10));
          }}
          style={{ background: `linear-gradient(to right, #3b82f6 ${progress}%, rgba(255,255,255,0.12) ${progress}%)` }}
        />
        <div className="cp-pb-slider-labels">
          <span>Start</span>
          <span>{progress.toFixed(0)}%</span>
          <span>End</span>
        </div>
      </div>

      <div className="cp-pb-controls">
        <button className="cp-pb-btn" onClick={() => skip(-1)} title="Back 5%">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M11 19V5l-7 7 7 7zm7-14v14l-7-7 7-7z"/></svg>
        </button>
        <button className="cp-pb-btn cp-pb-btn-stop" onClick={handleStop} title="Stop">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12"/></svg>
        </button>
        <button
          className={`cp-pb-btn cp-pb-btn-play ${isPlaying ? 'playing' : ''}`}
          onClick={isPlaying ? handlePause : handlePlay}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          )}
        </button>
        <button className="cp-pb-btn" onClick={() => skip(1)} title="Forward 5%">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5v14l7-7-7-7zm7 0v14l7-7-7-7z"/></svg>
        </button>
        <select
          className="cp-pb-speed"
          value={playbackSpeed}
          onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
        >
          <option value="0.5">0.5×</option>
          <option value="1">1×</option>
          <option value="2">2×</option>
          <option value="5">5×</option>
          <option value="10">10×</option>
        </select>
      </div>
    </div>
  );
}

/* ── Inline Analysis Panel ── */
const InlineAnalysis = ({ routeId, selectedDatapoint }) => {
  const { data, loading, error } = useRouteInfo(routeId);

  if (loading) {
    return (
      <div className="cp-analysis-state">
        <span className="cp-spinner" />
        <span>Loading route info…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cp-analysis-state cp-analysis-error">
        <span>⚠️ {error}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="cp-analysis-state">
        <span>No data available for this run.</span>
      </div>
    );
  }

  const formatDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m}m`;
  };

  const getScoreColor = (s) => {
    if (s >= 80) return '#4ade80';
    if (s >= 60) return '#a3e635';
    if (s >= 40) return '#facc15';
    if (s >= 20) return '#fb923c';
    return '#f87171';
  };

  const getScoreLabel = (s) => {
    if (s >= 80) return 'Excellent';
    if (s >= 60) return 'Good';
    if (s >= 40) return 'Fair';
    if (s >= 20) return 'Poor';
    return 'Very Poor';
  };

  return (
    <div className="cp-analysis">
      <div className="cp-an-header">
        <span className="cp-an-vessel">{data.vesselName}</span>
        <span className="cp-an-date">{data.date ? new Date(data.date).toLocaleDateString() : ''}</span>
      </div>
      <div className="cp-an-time">{data.startTime} – {data.endTime}</div>

      <div className="cp-an-grid">
        <div className="cp-an-stat">
          <span className="cp-an-stat-label">Distance</span>
          <span className="cp-an-stat-value">{data.distanceNm?.toFixed(2) ?? '—'} NM</span>
        </div>
        <div className="cp-an-stat">
          <span className="cp-an-stat-label">Duration</span>
          <span className="cp-an-stat-value">{data.durationMinutes ? formatDuration(data.durationMinutes) : '—'}</span>
        </div>
        <div className="cp-an-stat">
          <span className="cp-an-stat-label">Avg Speed</span>
          <span className="cp-an-stat-value">{data.avgSpeed?.toFixed(1) ?? '—'} kts</span>
        </div>
        <div className="cp-an-stat">
          <span className="cp-an-stat-label">Max Speed</span>
          <span className="cp-an-stat-value">{data.maxSpeed?.toFixed(1) ?? '—'} kts</span>
        </div>
      </div>

      {data.weatherSummary && (
        <Section title="Weather" icon="" defaultOpen={false}>
          <div className="cp-an-weather">
            <div className="cp-an-weather-row">
              <span>Avg Wind</span><span>{data.weatherSummary.avgWindSpeed?.toFixed(1)} kts</span>
            </div>
            <div className="cp-an-weather-row">
              <span>Max Wind</span><span>{data.weatherSummary.maxWindSpeed?.toFixed(1)} kts</span>
            </div>
            {data.weatherSummary.avgWaveHeight != null && (
              <div className="cp-an-weather-row">
                <span>Wave Height</span><span>{data.weatherSummary.avgWaveHeight.toFixed(1)} m</span>
              </div>
            )}
          </div>
        </Section>
      )}

      {data.deviationScore != null && (
        <Section title="Route Adherence" icon="">
          <div className="cp-an-deviation">
            <div className="cp-an-score" style={{ color: getScoreColor(data.deviationScore) }}>
              {data.deviationScore.toFixed(1)}
            </div>
            <div className="cp-an-score-label" style={{ color: getScoreColor(data.deviationScore) }}>
              {getScoreLabel(data.deviationScore)}
            </div>
            <div className="cp-an-bar-track">
              <div
                className="cp-an-bar-fill"
                style={{ width: `${data.deviationScore}%`, backgroundColor: getScoreColor(data.deviationScore) }}
              />
            </div>
            <span className="cp-an-bar-caption">SOP compliance</span>
          </div>
        </Section>
      )}

      {selectedDatapoint && (
        <Section title={`Datapoint ${selectedDatapoint.index + 1}`} icon="" defaultOpen={false}>
          <div className="cp-an-dp">
            <div className="cp-an-dp-row"><span>Lat</span><span>{selectedDatapoint.coordinates.latitude.toFixed(6)}°</span></div>
            <div className="cp-an-dp-row"><span>Lon</span><span>{selectedDatapoint.coordinates.longitude.toFixed(6)}°</span></div>
            {selectedDatapoint.speed != null && (
              <div className="cp-an-dp-row"><span>SOG</span><span>{selectedDatapoint.speed.toFixed(1)} kts</span></div>
            )}
            {selectedDatapoint.heading != null && (
              <div className="cp-an-dp-row"><span>HDG</span><span>{selectedDatapoint.heading.toFixed(1)}°</span></div>
            )}
            {selectedDatapoint.depth != null && (
              <div className="cp-an-dp-row"><span>Depth</span><span>{selectedDatapoint.depth.toFixed(1)} m</span></div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};

/* ══════════════════════════════════════
   Main ControlPanel
   ══════════════════════════════════════ */
const ControlPanel = ({
  vessels = [],
  selectedVessel,
  onVesselSelect,
  sopRoutes = [],
  selectedSopRoute,
  onSopRouteSelect,
  vesselRuns = [],
  selectedRun,
  onRunSelect,
  onToggleLayer,
  onOpenSettings,
  onOpenUpload,
  onOpenGeofenceManager,
  onOpenEnvironmental,
  isPlaybackActive,
  onPlaybackActiveChange,
  isPlaying,
  onPlayingChange,
  playbackIndex,
  onPlaybackIndexChange,
  layerVisibility = {},
  loading = false,
  selectedRunData,
  selectedDatapoint,
  // Search / filter props
  searchLoading = false,
  searchResultCount = null,
  isFiltered = false,
  onSearchRuns,
  onResetRunFilters,
  geofences = null,
}) => {
  const [activeTab, setActiveTab] = useState('routes');
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  const [showSegmentWindow, setShowSegmentWindow] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  const handleProcessRuns = useCallback(async () => {
    setIsProcessing(true);
    setProcessingStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/geofences/process-routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_all: false }),
      });
      if (!res.ok) throw new Error('Failed to process routes');
      const data = await res.json();
      setProcessingStatus({
        success: true,
        message: `Processed ${data.processed} routes, ${data.total_events} events, skipped ${data.skipped}`,
      });
    } catch (err) {
      setProcessingStatus({ success: false, message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleReprocessAll = useCallback(async () => {
    if (!window.confirm('This will delete all zone events and reprocess every run. Continue?')) return;
    setIsResetting(true);
    setProcessingStatus(null);
    try {
      // Step 1: Reset
      const resetRes = await fetch(`${API_BASE}/api/zone-events/reset-processing`, {
        method: 'POST',
      });
      if (!resetRes.ok) throw new Error('Failed to reset processing');
      const resetData = await resetRes.json();

      // Step 2: Reprocess all
      const processRes = await fetch(`${API_BASE}/api/geofences/process-routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_all: true }),
      });
      if (!processRes.ok) throw new Error('Failed to reprocess routes');
      const processData = await processRes.json();

      setProcessingStatus({
        success: true,
        message: `Reset ${resetData.events_deleted} events. Reprocessed ${processData.processed} runs, ${processData.total_events} new events.`,
      });
    } catch (err) {
      setProcessingStatus({ success: false, message: err.message });
    } finally {
      setIsResetting(false);
    }
  }, []);

  const handleSegmentComplete = useCallback((data) => {
    setShowSegmentWindow(false);
    window.location.reload();
  }, []);

  const handleApplyFilters = useCallback(({ filters, sortBy, sortDir }) => {
    if (onSearchRuns) {
      onSearchRuns({ filters, sortBy, sortDir });
    }
  }, [onSearchRuns]);

  const handleResetFilters = useCallback(() => {
    if (onResetRunFilters) {
      onResetRunFilters();
    }
  }, [onResetRunFilters]);

  if (panelCollapsed) {
    return (
      <button
        className="cp-collapsed-toggle"
        onClick={() => setPanelCollapsed(false)}
        title="Open control panel"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="8" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round"/>
          <line x1="8" y1="12" x2="21" y2="12" strokeWidth="2" strokeLinecap="round"/>
          <line x1="8" y1="18" x2="21" y2="18" strokeWidth="2" strokeLinecap="round"/>
          <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="2" strokeLinecap="round"/>
          <line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="2" strokeLinecap="round"/>
          <line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    );
  }

  const renderTab = () => {
    switch (activeTab) {

      case 'routes':
        return (
          <div className="cp-tab-content">
            <Section title="Vessel" icon="">
              <select className="cp-select" value={selectedVessel || ''} onChange={(e) => onVesselSelect(e.target.value || null)} disabled={loading}>
                <option value="">-- Choose Vessel --</option>
                {vessels.map((v) => (<option key={v.vessel_name} value={v.vessel_name}>{v.vessel_name} ({v.run_count} runs)</option>))}
              </select>
            </Section>

            <Section title="SOP Route" icon="">
              <select className="cp-select" value={selectedSopRoute || ''} onChange={(e) => onSopRouteSelect(e.target.value ? parseInt(e.target.value) : null)} disabled={loading}>
                <option value="">-- Choose SOP Route --</option>
                {sopRoutes.map((r) => (<option key={r.properties.route_id} value={r.properties.route_id}>{r.properties.route_name}</option>))}
              </select>
            </Section>

            {selectedVessel && (
              <Section title={`Vessel Runs${isFiltered ? ' (filtered)' : ''}`} icon="">
                {/* Filter button replaces the old <select> dropdown */}
                <RunFilterPanel
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  loading={searchLoading}
                  geofences={geofences}
                  resultCount={searchResultCount}
                  isFiltered={isFiltered}
                />

                {/* Scrollable run list */}
                {vesselRuns.length > 0 ? (
                  <div className="cp-run-list">
                    {vesselRuns.map((run) => (
                      <RunListItem
                        key={run.properties.run_id}
                        run={run}
                        isSelected={selectedRun === run.properties.run_id}
                        onClick={onRunSelect}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="cp-empty-hint">
                    {isFiltered ? 'No runs match filters.' : 'No runs found.'}
                  </div>
                )}
              </Section>
            )}

            <Section title="Layers" icon="" defaultOpen={false}>
              <div className="cp-layer-list">
                {Object.entries(LAYER_LABELS).map(([key, label]) => (
                  <label key={key} className="cp-layer-item">
                    <input type="checkbox" checked={layerVisibility[key] || false} onChange={() => onToggleLayer(key)} disabled={loading} />
                    <span className="cp-layer-dot" style={{ backgroundColor: LAYER_COLORS[key] }} />
                    <span className="cp-layer-label">{label}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Tools" icon="" defaultOpen={false}>
              <div className="cp-tools-list">
                <button className="cp-tool-btn" onClick={onOpenUpload}>
                  <span className="cp-tool-icon"></span>
                  <span className="cp-tool-label">Upload Data</span>
                </button>
                <button className="cp-tool-btn" onClick={onOpenGeofenceManager}>
                  <span className="cp-tool-icon"></span>
                  <span className="cp-tool-label">Manage Geofences</span>
                </button>
                <button className="cp-tool-btn" onClick={onOpenEnvironmental}>
                  <span className="cp-tool-icon"></span>
                  <span className="cp-tool-label">Environmental Data</span>
                </button>
              </div>
            </Section>

            {loading && (<div className="cp-loading"><span className="cp-spinner" />Loading…</div>)}
          </div>
        );

      case 'segmentation':
        return (
          <div className="cp-tab-content">
            <Section title="Segment Routes" icon="">
              <p className="cp-hint">Create runs by detecting zone entry/exit transitions on raw vessel GPS data.</p>
              <button className="cp-btn cp-btn-primary" onClick={() => setShowSegmentWindow(true)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                </svg>
                Open Segment Routes
              </button>
            </Section>

            <Section title="Process Runs" icon="">
              <p className="cp-hint">Detect zone events for all runs against the current geofence set.</p>
              <div className="cp-btn-group">
                <button className="cp-btn cp-btn-process" onClick={handleProcessRuns} disabled={isProcessing || isResetting}>
                  {isProcessing ? (<><span className="cp-spinner cp-spinner-sm" /> Processing…</>) : ('Process New')}
                </button>
                <button className="cp-btn cp-btn-danger" onClick={handleReprocessAll} disabled={isProcessing || isResetting}>
                  {isResetting ? (<><span className="cp-spinner cp-spinner-sm" /> Resetting…</>) : ('Reprocess All')}
                </button>
              </div>
              {processingStatus && (
                <div className={`cp-status ${processingStatus.success ? 'cp-status-ok' : 'cp-status-err'}`}>
                  {processingStatus.success ? '✓' : '✗'} {processingStatus.message}
                </div>
              )}
            </Section>
          </div>
        );

      case 'playback':
        return (
          <div className="cp-tab-content">
            {selectedRunData ? (
              <InlinePlayback
                route={selectedRunData}
                isPlaying={isPlaying}
                onPlayingChange={onPlayingChange}
                playbackIndex={playbackIndex}
                onPlaybackIndexChange={onPlaybackIndexChange}
                onPlaybackActiveChange={onPlaybackActiveChange}
              />
            ) : (
              <div className="cp-empty">
                <span className="cp-empty-icon">▶️</span>
                <p>Select a vessel run on the <strong>Routes</strong> tab to enable playback.</p>
              </div>
            )}
          </div>
        );

      case 'analysis':
        return (
          <div className="cp-tab-content">
            {selectedRun ? (
              <InlineAnalysis routeId={String(selectedRun)} selectedDatapoint={selectedDatapoint} />
            ) : (
              <div className="cp-empty">
                <span className="cp-empty-icon">📊</span>
                <p>Select a vessel run on the <strong>Routes</strong> tab to view analysis.</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="cp-panel">
        <div className="cp-header">
          <span className="cp-header-title">Control Panel</span>
          <div className="cp-header-actions">
            <button className="cp-icon-btn" onClick={onOpenSettings} title="Settings" aria-label="Open settings">⚙</button>
            <button className="cp-icon-btn" onClick={() => setPanelCollapsed(true)} title="Collapse panel" aria-label="Collapse panel">✕</button>
          </div>
        </div>

        <div className="cp-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`cp-tab ${activeTab === tab.id ? 'cp-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
            >
              <span className="cp-tab-icon">{tab.icon}</span>
              <span className="cp-tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="cp-body">{renderTab()}</div>
      </div>

      {showSegmentWindow && (
        <SegmentRoutesWindow onClose={() => setShowSegmentWindow(false)} onSegmentComplete={handleSegmentComplete} />
      )}
    </>
  );
};

export default ControlPanel;